// The socket mechanics both channels share: open with a connect timeout,
// close and error reporting, retry by the channel's policy, and dev logging
// to the event thread. Each channel client composes one and owns its messages.

import {
  ConnectionState,
  ErrorMessage,
  WebSocketErrorType,
} from '@airhead-games/shared/types';
import { createLogger } from '@airhead-games/shared/utilities';
import { EventRelay } from './eventRelay';
import {
  simpleEventStream,
  type StreamChannel,
  type StreamEntry,
} from '../../services/SimpleEventStream';

const logger = createLogger('reconnectingSocket');

const CONNECT_TIMEOUT_MS = 10000;

export interface ReconnectingSocketEvents {
  connectionState: {
    state: ConnectionState;
    code?: number;
    reason?: string;
  };
  error: ErrorMessage['payload'];
  open: undefined;
  message: unknown; // parsed JSON; the channel client validates its shape
}

export interface ReconnectingSocketOptions {
  channel: StreamChannel;
  maxAttempts: number;
  retryDelay: (attempt: number) => number; // attempt counts retries since the last open
  shouldRetry: (code: number, reason: string) => boolean;
}

export class ReconnectingSocket {
  private socket?: WebSocket;
  private url?: string;
  private attempts = 0;
  private manuallyClosed = false;
  private connectionTimeout?: ReturnType<typeof setTimeout>;
  private retryTimer?: ReturnType<typeof setTimeout>;
  private emitter = new EventRelay<ReconnectingSocketEvents>();

  on = this.emitter.on.bind(this.emitter);
  off = this.emitter.off.bind(this.emitter);

  constructor(private readonly options: ReconnectingSocketOptions) {}

  get ws(): WebSocket | undefined {
    return this.socket;
  }

  get isManuallyClosed(): boolean {
    return this.manuallyClosed;
  }

  get isOpen(): boolean {
    return this.socket?.readyState === WebSocket.OPEN;
  }

  connect(url: string) {
    this.url = url;
    this.manuallyClosed = false;
    this.open();
  }

  /** Closes for good: no retry follows, including one already scheduled. */
  disconnect() {
    this.manuallyClosed = true;
    clearTimeout(this.retryTimer);
    this.retryTimer = undefined;
    this.socket?.close();
  }

  /** Throws when the socket is not open; callers that may race a close check isOpen first. */
  send(json: string) {
    if (!this.socket) {
      logger.error(
        'Cannot send message: WebSocket is not initialized (ws is undefined)'
      );
      throw new Error('WebSocket is not initialized (ws is undefined)');
    }
    if (this.socket.readyState !== WebSocket.OPEN) {
      logger.error(
        `Cannot send message: WebSocket is not open (readyState: ${this.socket.readyState})`
      );
      throw new Error(
        'WebSocket is not open (readyState: ' + this.socket.readyState + ')'
      );
    }
    this.socket.send(json);
  }

  /** Dev: records an entry in the event thread, tagged with this channel. */
  pushDebugStream(entry: Omit<StreamEntry, 'id' | 'ts' | 'channel'>): void {
    try {
      simpleEventStream.push({ ...entry, channel: this.options.channel });
    } catch {
      // Ignore event-thread logging failures so they cannot interrupt send or dispatch.
    }
  }

  private open() {
    if (!this.url) throw new Error('Missing URL');

    logger.debug('Creating WebSocket with URL', this.url);
    const socket = new WebSocket(this.url);
    this.socket = socket;

    this.connectionTimeout = setTimeout(() => {
      if (this.socket?.readyState !== WebSocket.OPEN) {
        logger.debug('WebSocket connection timed out');
        this.emitConnectionState({
          state: ConnectionState.TIMEOUT,
          reason: 'Connection timeout',
        });
        this.socket?.close();
      }
    }, CONNECT_TIMEOUT_MS);

    socket.onopen = () => {
      logger.debug('WebSocket connection opened');
      this.attempts = 0;
      this.clearConnectionTimeout();
      this.emitConnectionState({ state: ConnectionState.CONNECTED });
      this.emitter.emit('open', undefined);
    };
    socket.onmessage = (event) => this.handleMessage(event.data);
    socket.onclose = (ev) => {
      logger.debug('WebSocket connection closed:', ev.code, ev.reason);
      this.socket = undefined;
      this.clearConnectionTimeout();
      this.emitConnectionState({
        state: ConnectionState.DISCONNECTED,
        code: ev.code,
        reason: ev.reason || 'Connection closed',
      });
      if (!this.manuallyClosed) this.tryReconnect(ev.code, ev.reason);
    };
    socket.onerror = (err) => {
      logger.error('WebSocket error:', err);
      this.socket = undefined;
      this.emitter.emit('error', {
        errorType: WebSocketErrorType.INTERNAL_ERROR,
        message: 'WebSocket error',
        details: err,
      });
    };
  }

  private tryReconnect(code: number, reason: string) {
    if (!this.options.shouldRetry(code, reason)) {
      logger.debug('Connection failed with non-retryable error:', {
        code,
        reason,
      });
      // FAILED for a first connection that cannot succeed, RECONNECT_FAILED
      // when a reconnection gives up
      this.emitConnectionState({
        state:
          this.attempts === 0
            ? ConnectionState.FAILED
            : ConnectionState.RECONNECT_FAILED,
        reason: reason || 'Connection failed',
        code,
      });
      return;
    }

    if (this.attempts < this.options.maxAttempts) {
      this.retryTimer = setTimeout(() => {
        this.retryTimer = undefined;
        this.attempts++;
        this.open();
      }, this.options.retryDelay(this.attempts));
      this.emitConnectionState({
        state: ConnectionState.RECONNECTING,
        reason: 'reconnect_attempt',
      });
    } else {
      this.emitConnectionState({
        state: ConnectionState.RECONNECT_FAILED,
        reason: 'max_attempts',
      });
    }
  }

  private handleMessage(data: string) {
    let message: unknown;
    try {
      message = JSON.parse(data);
    } catch {
      logger.error('Received invalid JSON from server');
      this.emitter.emit('error', {
        errorType: WebSocketErrorType.INVALID_MESSAGE,
        message: 'Invalid JSON',
        details: data,
      });
      return;
    }
    this.emitter.emit('message', message);
  }

  private emitConnectionState(
    payload: ReconnectingSocketEvents['connectionState']
  ) {
    this.pushDebugStream({
      direction: 'in',
      kind: 'connection',
      type: 'SOCKET_STATE',
      action: payload.state,
      raw: payload,
    });
    this.emitter.emit('connectionState', payload);
  }

  private clearConnectionTimeout() {
    clearTimeout(this.connectionTimeout);
    this.connectionTimeout = undefined;
  }
}
